import { FunctionComponent, useEffect, useState } from "react";

import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";

import { format } from "date-fns";

import { faPlus, faTrashCan, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { ListEventType } from "@signalist/shared-types";

import { Modal } from "@/components";
import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";

import { EventOutreachSchemaType } from "../../schema/list";

/** Shape the strip sends to the upsert mutation. */
export interface EventFormPayload {
	eventName: string;
	listType: ListEventType;
	startDate: string;
	endDate: string;
	meetingLocation?: string;
	meetingLink?: string;
	meetingTimes: string[];
	customVariables: { key: string; value: string }[];
}

interface EventDetailsModalProps {
	isOpened: boolean;
	isLoading: boolean;
	event?: EventOutreachSchemaType | null;
	title?: string;
	onClose: () => void;
	onSave: (payload: EventFormPayload) => void;
}

const LIST_TYPE_OPTIONS = [
	{ title: "Attendees / Speakers", value: ListEventType.ATTENDEES },
	{ title: "Exhibitors", value: ListEventType.EXHIBITORS },
	{ title: "Mixed / Other", value: ListEventType.MIXED },
];

type FieldErrors = Partial<Record<"eventName" | "listType" | "dates", string>>;

/** Small monospace token tag shown next to a label, e.g. {event_name}. */
const VarTag: FunctionComponent<{ token: string }> = ({ token }) => (
	<span className="ml-2 font-mono text-xs text-brand-light bg-blue-action-200 px-1.5 py-0.5 rounded">
		{`{${token}}`}
	</span>
);

export const EventDetailsModal: FunctionComponent<EventDetailsModalProps> = ({
	isOpened,
	isLoading,
	event,
	title = "Event details",
	onClose,
	onSave,
}) => {
	const [eventName, setEventName] = useState("");
	const [listType, setListType] = useState<ListEventType | "">("");
	const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
		startDate: null,
		endDate: null,
	});
	const [meetingLocation, setMeetingLocation] = useState("");
	const [meetingLink, setMeetingLink] = useState("");
	const [meetingTimes, setMeetingTimes] = useState<string[]>([]);
	const [timeInput, setTimeInput] = useState("");
	const [customVariables, setCustomVariables] = useState<{ key: string; value: string }[]>([]);
	const [errors, setErrors] = useState<FieldErrors>({});

	// Re-seed the form whenever it opens so edits start from the persisted event.
	useEffect(() => {
		if (!isOpened) {
			return;
		}

		setEventName(event?.eventName ?? "");
		setListType((event?.listType as ListEventType) ?? "");
		setDateRange({
			startDate: event?.startDate ? new Date(event.startDate) : null,
			endDate: event?.endDate ? new Date(event.endDate) : null,
		});
		setMeetingLocation(event?.meetingLocation ?? "");
		setMeetingLink(event?.meetingLink ?? "");
		setMeetingTimes(event?.meetingTimes ?? []);
		setCustomVariables(event?.customVariables ?? []);
		setTimeInput("");
		setErrors({});
	}, [isOpened, event]);

	const addTime = () => {
		const value = timeInput.trim();

		if (value && !meetingTimes.includes(value)) {
			setMeetingTimes([...meetingTimes, value]);
		}

		setTimeInput("");
	};

	const removeTime = (index: number) => setMeetingTimes(meetingTimes.filter((_, i) => i !== index));

	const updateCustomVariable = (index: number, field: "key" | "value", value: string) =>
		setCustomVariables(
			customVariables.map((cv, i) => (i === index ? { ...cv, [field]: value } : cv))
		);

	const addCustomVariable = () => setCustomVariables([...customVariables, { key: "", value: "" }]);

	const removeCustomVariable = (index: number) =>
		setCustomVariables(customVariables.filter((_, i) => i !== index));

	const validate = (): FieldErrors => {
		const next: FieldErrors = {};

		if (!eventName.trim()) {
			next.eventName = "Event name is required";
		}

		if (!listType) {
			next.listType = "List type is required";
		}

		if (!dateRange.startDate || !dateRange.endDate) {
			next.dates = "Start and end date are required";
		}

		return next;
	};

	const handleSave = () => {
		const validationErrors = validate();

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);

			return;
		}

		onSave({
			eventName: eventName.trim(),
			listType: listType as ListEventType,
			startDate: format(dateRange.startDate as Date, "yyyy-MM-dd"),
			endDate: format(dateRange.endDate as Date, "yyyy-MM-dd"),
			meetingLocation: meetingLocation.trim() || undefined,
			meetingLink: meetingLink.trim() || undefined,
			meetingTimes,
			// Only keep rows the user actually labelled — the key IS the token name.
			customVariables: customVariables
				.map((cv) => ({ key: cv.key.trim(), value: cv.value.trim() }))
				.filter((cv) => cv.key),
		});
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSave}
			isLoading={isLoading}
			isOpened={isOpened}
			overflow
			size="md"
			submitButtonText="Save event details"
			subtitle="These values become variables you can drop into any campaign step. Type them once — they apply to every campaign on this list."
			title={title}
		>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-1.5">
					<label className="text-sm font-medium text-gray-900" htmlFor="event-name">
						Event name
						<VarTag token="event_name" />
					</label>
					<InputField
						error={errors.eventName}
						handleChange={(e) => setEventName(e.target.value)}
						name="event-name"
						placeholder="e.g. Hinterland of Things 2026"
						showError={!!errors.eventName}
						value={eventName}
					/>
				</div>

				<SelectField
					error={errors.listType}
					handleSelect={(value) => setListType((value as ListEventType) ?? "")}
					label="List type"
					options={LIST_TYPE_OPTIONS}
					placeholder="Select…"
					selectedOption={listType || null}
					showError={!!errors.listType}
				/>

				<div className="flex flex-col gap-1.5">
					<label className="text-sm font-medium text-gray-900" htmlFor="event-dates">
						Event dates
						<VarTag token="event_start" />
						<VarTag token="event_end" />
					</label>
					<Datepicker
						containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
						displayFormat="DD.MM.YYYY"
						inputClassName="relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring focus:border-blue-500 focus:ring-blue-500/20"
						placeholder="Start date - End date"
						primaryColor="blue"
						separator=" – "
						useRange
						value={dateRange}
						onChange={(value: DateValueType) =>
							setDateRange({
								startDate: value?.startDate ? new Date(value.startDate) : null,
								endDate: value?.endDate ? new Date(value.endDate) : null,
							})
						}
					/>
					{errors.dates ? <span className="text-xs text-status-error">{errors.dates}</span> : null}
					<span className="text-xs text-gray-500">
						Campaigns stop sending at the end date by default. You can override per campaign.
					</span>
				</div>

				<div className="border-t border-border-light pt-4 flex flex-col gap-4">
					<div className="flex flex-col gap-1.5">
						<label className="text-sm font-medium text-gray-900" htmlFor="meeting-location">
							Meeting location
							<VarTag token="meeting_location" />
						</label>
						<InputField
							handleChange={(e) => setMeetingLocation(e.target.value)}
							name="meeting-location"
							placeholder="e.g. Hall A, Booth 204"
							value={meetingLocation}
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className="text-sm font-medium text-gray-900" htmlFor="meeting-link">
							Meeting link
							<VarTag token="meeting_link" />
						</label>
						<InputField
							handleChange={(e) => setMeetingLink(e.target.value)}
							name="meeting-link"
							placeholder="e.g. https://cal.com/your-team/event"
							value={meetingLink}
						/>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className="text-sm font-medium text-gray-900" htmlFor="meeting-time">
							Meeting times
							<VarTag token="meeting_time" />
						</label>
						<div className="flex flex-wrap items-center gap-2 rounded-xl border border-border-light p-2">
							{meetingTimes.map((time, index) => (
								<span
									key={`${time}-${index}`}
									className="inline-flex items-center gap-1.5 rounded-md bg-blue-action-200 px-2 py-1 text-xs font-medium text-brand-light"
								>
									{time}
									<button
										aria-label={`Remove ${time}`}
										className="text-brand-light/70 hover:text-brand-light"
										type="button"
										onClick={() => removeTime(index)}
									>
										<Icon icon={faXmark} />
									</button>
								</span>
							))}
							<input
								className="flex-1 min-w-[100px] border-none p-1 text-sm outline-none"
								placeholder="Add a time… (press Enter)"
								value={timeInput}
								onChange={(e) => setTimeInput(e.target.value)}
								onKeyDown={(e) => {
									// Commit on Enter only — a comma is a legitimate character inside a slot
									// (e.g. "01.07., 10h"), so it must not split the value into a new chip.
									if (e.key === "Enter") {
										e.preventDefault();
										addTime();
									}
								}}
							/>
						</div>
						<span className="text-xs text-gray-500">
							Randomly distributed across leads, so some may receive the same slot.
						</span>
					</div>
				</div>

				<div className="border-t border-border-light pt-4 flex flex-col gap-2">
					<span className="text-sm font-medium text-gray-900">Custom variables</span>
					{customVariables.map((cv, index) => (
						<div key={index} className="flex items-center gap-2">
							<div className="flex-1">
								<InputField
									handleChange={(e) => updateCustomVariable(index, "key", e.target.value)}
									name={`cvar-key-${index}`}
									placeholder="Label (e.g. hall)"
									value={cv.key}
								/>
							</div>
							<div className="flex-1">
								<InputField
									handleChange={(e) => updateCustomVariable(index, "value", e.target.value)}
									name={`cvar-value-${index}`}
									placeholder="Value (e.g. Halle A)"
									value={cv.value}
								/>
							</div>
							<button
								aria-label="Remove custom variable"
								className="p-2 text-gray-500 hover:text-status-error"
								type="button"
								onClick={() => removeCustomVariable(index)}
							>
								<Icon icon={faTrashCan} />
							</button>
						</div>
					))}
					<button
						className="flex w-fit items-center gap-1.5 text-sm font-medium text-brand-light"
						type="button"
						onClick={addCustomVariable}
					>
						<Icon icon={faPlus} /> Add custom variable
					</button>
				</div>
			</div>
		</Modal>
	);
};
